import React, { useState, useEffect } from 'react';
import { X, Minus, Plus, Trash2, ShoppingBag, Download, Send, LogIn, Check, Package, MessageCircle, FileText, ArrowLeft, Cloud, HardDrive, MapPin, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useCart } from '../context/CartContext';
import { useCustomer } from '../context/CustomerContext';
import { generatePdfFromCart, createQuote } from '../services/api';
import { toast } from 'sonner';

// Phone mask function
const formatPhone = (value) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 2) return numbers;
  if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  if (numbers.length <= 11) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
  return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
};

// CEP mask function
const formatCep = (value) => {
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 5) return numbers;
  return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
};

// Validate phone (Brazilian format: 11 digits with DDD)
const isValidPhone = (phone) => {
  const numbers = phone.replace(/\D/g, '');
  return numbers.length === 10 || numbers.length === 11;
};

// Validate CEP (8 digits)
const isValidCep = (cep) => {
  const numbers = cep.replace(/\D/g, '');
  return numbers.length === 8;
};

const CartDrawer = ({ settings, onOpenCustomerModal }) => {
  const { items, isOpen, closeCart, updateQuantity, updateNotes, removeItem, clearCart, getTotalItems, isCustomerLoggedIn, isSyncing } = useCart();
  const { customer, isLoggedIn } = useCustomer();
  
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerCep, setCustomerCep] = useState('');
  const [address, setAddress] = useState(null);
  const [manualStreet, setManualStreet] = useState('');
  const [loadingCep, setLoadingCep] = useState(false);
  const [addressNumber, setAddressNumber] = useState('');
  const [addressComplement, setAddressComplement] = useState('');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState('');
  
  // Pre-fill form when customer is logged in
  useEffect(() => {
    if (isLoggedIn && customer) {
      setCustomerName(customer.name || '');
      setCustomerPhone(customer.phone ? formatPhone(customer.phone) : '');
      if (customer.cep) {
        setCustomerCep(formatCep(customer.cep));
      }
    }
  }, [isLoggedIn, customer]);

  // Fetch address when CEP is complete
  useEffect(() => {
    const fetchAddress = async () => {
      const cleanCep = customerCep.replace(/\D/g, '');
      if (cleanCep.length !== 8) {
        setAddress(null);
        return;
      }
      
      setLoadingCep(true);
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        const data = await response.json();
        
        if (data.erro) {
          setAddress(null);
          toast.error('CEP não encontrado');
        } else {
          setManualStreet('');
          setAddress({
            street: data.logradouro,
            neighborhood: data.bairro,
            city: data.localidade,
            state: data.uf
          });
        }
      } catch (error) {
        console.error('Error fetching CEP:', error);
        setAddress(null);
        toast.error('Erro ao buscar CEP');
      } finally {
        setLoadingCep(false);
      }
    };
    
    const timeoutId = setTimeout(fetchAddress, 500);
    return () => clearTimeout(timeoutId);
  }, [customerCep]);

  const validateForm = () => {
    if (!customerName.trim()) {
      toast.error('Por favor, preencha seu nome.');
      return false;
    }
    if (!customerPhone.trim()) {
      toast.error('Por favor, preencha seu WhatsApp.');
      return false;
    }
    if (!isValidPhone(customerPhone)) {
      toast.error('Número de WhatsApp inválido. Use o formato (00) 00000-0000');
      return false;
    }
    if (!customerCep.trim()) {
      toast.error('Por favor, preencha seu CEP.');
      return false;
    }
    if (!isValidCep(customerCep)) {
      toast.error('CEP inválido. Use o formato 00000-000');
      return false;
    }
    if (!address) {
      toast.error('CEP não encontrado. Verifique e tente novamente.');
      return false;
    }
    if (!address.street && !manualStreet.trim()) {
      toast.error('Por favor, informe o nome da rua.');
      return false;
    }
    if (!addressNumber.trim()) {
      toast.error('Por favor, informe o número do endereço.');
      return false;
    }
    return true;
  };

  const getCartData = () => ({
    items,
    customer_name: customerName.trim(),
    customer_phone: customerPhone.replace(/\D/g, ''),
    customer_cep: customerCep.replace(/\D/g, ''),
    customer_address: address ? {
      street: address.street || manualStreet.trim(),
      number: addressNumber.trim() || null,
      complement: addressComplement.trim() || null,
      neighborhood: address.neighborhood,
      city: address.city,
      state: address.state
    } : null,
    notes: notes.trim() || null,
    customer_id: customer?.id || null
  });

  // Format items for WhatsApp text message
  const formatWhatsAppMessage = () => {
    let message = `*SOLICITAÇÃO DE ORÇAMENTO*\n`;
    message += `━━━━━━━━━━━━━━━━━━━\n\n`;
    message += `*Cliente:* ${customerName}\n`;
    message += `*WhatsApp:* ${customerPhone}\n`;
    if (address) {
      message += `*Endereço:* ${address.street || manualStreet}`;
      if (addressNumber) message += `, ${addressNumber}`;
      if (addressComplement) message += ` - ${addressComplement}`;
      message += `\n`;
      message += `*Bairro:* ${address.neighborhood}\n`;
      message += `*Cidade:* ${address.city}/${address.state}\n`;
      message += `*CEP:* ${customerCep}\n`;
    }
    message += `\n*ITENS DO PEDIDO:*\n`;
    message += `━━━━━━━━━━━━━━━━━━━\n`;
    
    let totalGeral = 0;
    let hasAnyPrice = false;

    items.forEach((item, idx) => {
      message += `\n${idx + 1}. *${item.product_name}*\n`;
      message += `   Quantidade: ${item.quantity} un.\n`;
      
      if (item.unit_price && item.unit_price > 0) {
        hasAnyPrice = true;
        const subtotal = item.unit_price * item.quantity;
        totalGeral += subtotal;
        message += `   Preço unit.: R$ ${item.unit_price.toFixed(2)}\n`;
        message += `   Subtotal: R$ ${subtotal.toFixed(2)}\n`;
      }
      
      if (item.customization_notes) {
        message += `   Obs: ${item.customization_notes}\n`;
      }
    });

    if (hasAnyPrice) {
      message += `\n━━━━━━━━━━━━━━━━━━━\n`;
      message += `*TOTAL ESTIMADO: R$ ${totalGeral.toFixed(2)}*\n`;
    }
    
    if (notes) {
      message += `\n━━━━━━━━━━━━━━━━━━━\n`;
      message += `*OBSERVAÇÕES GERAIS:*\n`;
      message += `${notes}\n`;
    }
    
    message += `\n━━━━━━━━━━━━━━━━━━━\n`;
    message += hasAnyPrice 
      ? `_Valores sujeitos a confirmação._`
      : `_Aguardo retorno com valores._`;
    
    return message;
  };

  // Send quote only (no WhatsApp)
  const handleSendQuoteOnly = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    setLoadingAction('quote');
    
    try {
      const cartData = getCartData();
      await createQuote(cartData);
      
      toast.success('Orçamento enviado com sucesso! Entraremos em contato em breve.');
      
      // Clear cart after sending
      clearCart();
      resetForm();
      closeCart();
      
    } catch (error) {
      console.error('Error sending quote:', error);
      toast.error('Erro ao enviar orçamento. Tente novamente.');
    } finally {
      setIsLoading(false);
      setLoadingAction('');
    }
  };

  // Send via WhatsApp AND save quote
  const handleSendWhatsApp = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    setLoadingAction('whatsapp');
    
    try {
      // Save quote to database
      const cartData = getCartData();
      await createQuote(cartData);
      
      // Open WhatsApp with formatted message
      const message = formatWhatsAppMessage();
      const whatsappUrl = `https://wa.me/${settings?.whatsapp || '5555984551621'}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
      toast.success('Orçamento salvo e WhatsApp aberto!');
      
      // Clear cart after sending
      clearCart();
      resetForm();
      closeCart();
      
    } catch (error) {
      console.error('Error sending to WhatsApp:', error);
      toast.error('Erro ao enviar. Tente novamente.');
    } finally {
      setIsLoading(false);
      setLoadingAction('');
    }
  };

  // Download PDF
  const handleDownloadPdf = async () => {
    if (!validateForm()) return;
    
    setIsLoading(true);
    setLoadingAction('pdf');
    
    try {
      const cartData = getCartData();
      
      // Save quote to database
      await createQuote(cartData);
      
      // Generate and download PDF
      const pdfBlob = await generatePdfFromCart(cartData);
      
      const url = window.URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `orcamento_gelatti_${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF baixado com sucesso!');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Erro ao gerar PDF. Tente novamente.');
    } finally {
      setIsLoading(false);
      setLoadingAction('');
    }
  };

  const resetForm = () => {
    if (!isLoggedIn) {
      setCustomerName('');
      setCustomerPhone('');
      setCustomerCep('');
      setAddress(null);
      setAddressNumber('');
      setAddressComplement('');
    }
    setNotes('');
  };

  // Calculate total
  const total = items.reduce((acc, item) => acc + (item.unit_price || 0) * item.quantity, 0);
  const hasAnyPrice = items.some(item => item.unit_price && item.unit_price > 0);

  if (!isOpen) return null;

  return (
    <>
      {/* Full Screen Overlay */}
      <div className="fixed inset-0 bg-slate-100 z-50 overflow-y-auto">
        {/* Header */}
        <header className="bg-[#195C75] text-white sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button 
                  onClick={closeCart}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-xl font-bold">Checkout</h1>
                  <div className="flex items-center gap-2 text-sm text-white/80">
                    <span>{getTotalItems()} {getTotalItems() === 1 ? 'item' : 'itens'} no carrinho</span>
                    {isCustomerLoggedIn ? (
                      <span className="flex items-center gap-1 text-xs bg-white/20 px-2 py-0.5 rounded-full">
                        {isSyncing ? (
                          <>
                            <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                            Salvando...
                          </>
                        ) : (
                          <>
                            <Cloud className="w-3 h-3" />
                            Salvo na nuvem
                          </>
                        )}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs bg-white/10 px-2 py-0.5 rounded-full">
                        <HardDrive className="w-3 h-3" />
                        Salvo localmente
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={closeCart}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 py-6">
          {items.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center shadow-sm">
              <ShoppingBag className="w-20 h-20 mx-auto mb-4 text-slate-300" />
              <h2 className="text-xl font-semibold text-slate-700 mb-2">Carrinho vazio</h2>
              <p className="text-slate-500 mb-6">Adicione produtos para solicitar orçamento</p>
              <Button onClick={closeCart} className="bg-[#195C75] hover:bg-[#195C75]/90">
                Continuar Comprando
              </Button>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Items List - Left Side */}
              <div className="lg:col-span-2 space-y-4">
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="p-4 border-b bg-slate-50">
                    <h2 className="font-semibold text-slate-800">Itens do Pedido</h2>
                  </div>
                  <div className="divide-y">
                    {items.map((item, index) => (
                      <div key={index} className="p-4">
                        <div className="flex gap-4">
                          {item.image_url && (
                            <div className="w-20 h-20 flex-shrink-0">
                              {item.image_adjustment ? (
                                <div 
                                  className="w-20 h-20 rounded-full overflow-hidden border-2 border-[#195C75]/30"
                                  style={{ 
                                    backgroundImage: `url(${item.image_url})`,
                                    backgroundSize: `${item.image_adjustment.scale * 100}%`,
                                    backgroundPosition: `${50 + item.image_adjustment.x}% ${50 + item.image_adjustment.y}%`,
                                    backgroundRepeat: 'no-repeat'
                                  }}
                                />
                              ) : (
                                <img 
                                  src={item.image_url} 
                                  alt={item.product_name}
                                  className="w-20 h-20 object-cover rounded-lg"
                                />
                              )}
                            </div>
                          )}
                          <div className="flex-1">
                            <h3 className="font-medium text-slate-800">{item.product_name}</h3>
                            
                            {/* Price Info */}
                            {item.unit_price && item.unit_price > 0 && (
                              <div className="text-sm text-[#195C75] mt-1">
                                <span>R$ {item.unit_price.toFixed(2).replace('.', ',')} un.</span>
                                <span className="mx-1">×</span>
                                <span>{item.quantity}</span>
                                <span className="mx-1">=</span>
                                <span className="font-semibold">R$ {(item.unit_price * item.quantity).toFixed(2).replace('.', ',')}</span>
                              </div>
                            )}
                            
                            {/* Quantity Controls */}
                            <div className="flex items-center gap-3 mt-3">
                              <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
                                <button
                                  onClick={() => updateQuantity(index, item.quantity - 1)}
                                  className="w-8 h-8 flex items-center justify-center bg-white rounded-md border border-slate-200 hover:bg-slate-50"
                                >
                                  <Minus className="w-4 h-4" />
                                </button>
                                <span className="w-12 text-center font-medium">{item.quantity}</span>
                                <button
                                  onClick={() => updateQuantity(index, item.quantity + 1)}
                                  className="w-8 h-8 flex items-center justify-center bg-white rounded-md border border-slate-200 hover:bg-slate-50"
                                >
                                  <Plus className="w-4 h-4" />
                                </button>
                              </div>
                              <button
                                onClick={() => removeItem(index)}
                                className="text-red-500 hover:text-red-600 text-sm flex items-center gap-1"
                              >
                                <Trash2 className="w-4 h-4" />
                                <span className="hidden sm:inline">Remover</span>
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Notes for this item */}
                        <Input
                          placeholder="Observações deste item (cores, arte, etc.)"
                          value={item.customization_notes || ''}
                          onChange={(e) => updateNotes(index, e.target.value)}
                          className="mt-3 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* General Notes */}
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Observações Gerais do Pedido (opcional)
                  </label>
                  <Textarea
                    placeholder="Ex: Entregar até dia 15/01, cores vibrantes, etc..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    data-testid="cart-notes"
                  />
                </div>
              </div>

              {/* Summary & Actions - Right Side */}
              <div className="lg:col-span-1 space-y-4">
                {/* Customer Info */}
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-800">Seus Dados</h3>
                    
                    {isLoggedIn ? (
                      <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                        <Check className="w-3 h-3" />
                        <span>Logado</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          closeCart();
                          if (onOpenCustomerModal) onOpenCustomerModal();
                        }}
                        className="flex items-center gap-1 text-xs text-[#195C75] hover:text-[#157A8C] font-medium"
                        data-testid="cart-login-btn"
                      >
                        <LogIn className="w-3 h-3" />
                        Entrar
                      </button>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Nome completo *</label>
                      <Input
                        placeholder="Seu nome"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        required
                        data-testid="cart-customer-name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">WhatsApp (com DDD) *</label>
                      <Input
                        placeholder="(00) 00000-0000"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(formatPhone(e.target.value))}
                        maxLength={15}
                        required
                        data-testid="cart-customer-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">CEP *</label>
                      <div className="relative">
                        <Input
                          placeholder="00000-000"
                          value={customerCep}
                          onChange={(e) => setCustomerCep(formatCep(e.target.value))}
                          maxLength={9}
                          required
                          data-testid="cart-customer-cep"
                        />
                        {loadingCep && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <Loader2 className="w-4 h-4 text-[#195C75] animate-spin" />
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Address Display */}
                    {address && (
                      <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            {address.street && <p className="font-medium text-green-800">{address.street}</p>}
                            {address.neighborhood && <p className="text-green-700">{address.neighborhood}</p>}
                            <p className="text-green-700">{address.city}/{address.state}</p>
                          </div>
                        </div>
                        
                        {/* Manual street when CEP doesn't return one */}
                        {!address.street && (
                          <div className="mt-3">
                            <label className="block text-xs text-green-700 mb-1">Rua *</label>
                            <Input
                              placeholder="Nome da rua"
                              value={manualStreet}
                              onChange={(e) => setManualStreet(e.target.value)}
                              className="h-8 text-sm bg-white"
                              data-testid="cart-customer-street"
                            />
                          </div>
                        )}
                        
                        {/* Number and Complement */}
                        <div className="grid grid-cols-2 gap-2 mt-3">
                          <div>
                            <label className="block text-xs text-green-700 mb-1">Número *</label>
                            <Input
                              placeholder="Nº"
                              value={addressNumber}
                              onChange={(e) => setAddressNumber(e.target.value)}
                              className="h-8 text-sm bg-white"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-green-700 mb-1">Complemento</label>
                            <Input
                              placeholder="Apto, Sala..."
                              value={addressComplement}
                              onChange={(e) => setAddressComplement(e.target.value)}
                              className="h-8 text-sm bg-white"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Order Summary */}
                <div className="bg-white rounded-xl shadow-sm p-4">
                  <h3 className="font-semibold text-slate-800 mb-4">Resumo</h3>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Itens</span>
                      <span>{getTotalItems()} unidades</span>
                    </div>
                    {hasAnyPrice && (
                      <>
                        <div className="flex justify-between pt-2 border-t">
                          <span className="font-semibold text-slate-800">Total Estimado</span>
                          <span className="font-bold text-lg text-[#195C75]">
                            R$ {total.toFixed(2).replace('.', ',')}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400">Valores sujeitos a confirmação</p>
                      </>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
                  {/* Primary: Send Quote Only */}
                  <Button
                    onClick={handleSendQuoteOnly}
                    disabled={isLoading}
                    className="w-full bg-[#195C75] hover:bg-[#195C75]/90 py-5 text-base"
                    data-testid="cart-send-quote"
                  >
                    {isLoading && loadingAction === 'quote' ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <FileText className="w-5 h-5 mr-2" />
                        Enviar Orçamento
                      </>
                    )}
                  </Button>

                  {/* Secondary: WhatsApp */}
                  <Button
                    onClick={handleSendWhatsApp}
                    disabled={isLoading}
                    variant="outline"
                    className="w-full border-green-500 text-green-600 hover:bg-green-50 py-5 text-base"
                    data-testid="cart-send-whatsapp"
                  >
                    {isLoading && loadingAction === 'whatsapp' ? (
                      <>
                        <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin mr-2" />
                        Abrindo...
                      </>
                    ) : (
                      <>
                        <MessageCircle className="w-5 h-5 mr-2" />
                        WhatsApp
                      </>
                    )}
                  </Button>

                  {/* Tertiary: Download PDF */}
                  <Button
                    onClick={handleDownloadPdf}
                    disabled={isLoading}
                    variant="ghost"
                    className="w-full text-slate-600 hover:text-slate-800 hover:bg-slate-100"
                    data-testid="cart-download-pdf"
                  >
                    {isLoading && loadingAction === 'pdf' ? (
                      <>Gerando PDF...</>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Baixar PDF
                      </>
                    )}
                  </Button>

                  <div className="pt-2 border-t">
                    <button
                      onClick={clearCart}
                      className="w-full text-slate-400 hover:text-red-500 text-sm py-2 transition-colors"
                      data-testid="cart-clear"
                    >
                      Limpar carrinho
                    </button>
                  </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Package className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="text-sm">
                      <p className="font-medium text-blue-800 mb-1">Como funciona?</p>
                      <ul className="text-blue-600 space-y-1 text-xs">
                        <li><strong>Enviar Orçamento:</strong> Salvamos seu pedido e entraremos em contato</li>
                        <li><strong>WhatsApp:</strong> Abre conversa direta + salva o orçamento</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default CartDrawer;
